import apiService from "../../service/Api";

export type ItemKind = "proposal" | "assignment";
export type JobCardQueue = "proposal" | "assignment" | "task" | "report";

export interface Page<T> {
  rows: T[];
  count: number;
}

const BASE = "/api/job-card/job-cards";
const PATHS: Record<JobCardQueue, string> = {
  proposal: `${BASE}/proposals`,
  assignment: `${BASE}/assignments`,
  task: `${BASE}/entries`,
  report: `${BASE}/reports`,
};

export const QUEUE_LABEL: Record<JobCardQueue, string> = {
  proposal: "Proposals",
  assignment: "Assignments",
  task: "Tasks",
  report: "Reports",
};

export const QUEUE_STATUS: Record<JobCardQueue, { value: string; label: string }[]> = {
  proposal: [
    { value: "pending", label: "Pending review" },
    { value: "needs_revision", label: "Needs revision" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ],
  assignment: [
    { value: "completed", label: "Awaiting review" },
    { value: "sent", label: "Sent" },
    { value: "accepted", label: "Accepted" },
    { value: "needs_revision", label: "Needs revision" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ],
  task: [
    { value: "pending", label: "Pending review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ],
  report: [
    { value: "draft", label: "Draft" },
    { value: "finalised", label: "Finalised" },
  ],
};

export const DEFAULT_STATUS: Record<JobCardQueue, string> = {
  proposal: "pending",
  assignment: "completed",
  task: "pending",
  report: "draft",
};

export const isQueue = (v: string | null): v is JobCardQueue =>
  v === "proposal" || v === "assignment" || v === "task" || v === "report";

const num = (v: string | number | null | undefined): number | null =>
  v === null || v === undefined || v === "" ? null : Number(v);

interface ListParams {
  status?: string;
  search?: string;
  type?: string;
  page: number;
  pageSize: number;
}

const list = async <R, T>(
  queue: JobCardQueue,
  p: ListParams,
  map: (r: R) => T,
): Promise<Page<T>> => {
  const params: Record<string, string | number> = {
    page: p.page + 1,
    page_size: p.pageSize,
  };
  if (p.status) params.status = p.status;
  if (p.search) params.search = p.search;
  if (p.type) params.report_type = p.type;
  const res = await apiService.get(`${PATHS[queue]}/`, { params });
  const data = res.data as R[] | { results?: R[]; count?: number };
  if (Array.isArray(data)) return { rows: data.map(map), count: data.length };
  return { rows: (data.results ?? []).map(map), count: data.count ?? 0 };
};

const one = async <R, T>(queue: JobCardQueue, id: string | number, map: (r: R) => T): Promise<T> => {
  const res = await apiService.get(`${PATHS[queue]}/${id}/`);
  return map(res.data as R);
};

export const fetchCount = async (queue: JobCardQueue, status: string): Promise<number> =>
  (await list<unknown, unknown>(queue, { status, page: 0, pageSize: 1 }, (r) => r)).count;

// ----------------------------------------------- proposals & assignments ----

export interface ReviewItem {
  key: string;
  id: number;
  kind: ItemKind;
  title: string;
  description: string;
  internName: string;
  internPicture: string | null;
  dueDate: string | null;
  reward: number;
  hours: number | null;
  hourlyRate: number | null;
  status: string;
  feedback: string;
  rejectionReason: string;
  createdAt: string;
}

interface RawItem {
  id: number;
  title: string | null;
  description: string | null;
  intern_name: string;
  intern_profile_picture?: string | null;
  intern_hourly_rate?: string | number | null;
  due_date?: string | null;
  deadline?: string | null;
  reward?: string | number | null;
  estimated_reward?: string | number | null;
  hours?: string | number | null;
  status: string;
  feedback?: string | null;
  rejection_reason?: string | null;
  created_at: string;
}

const toItem = (kind: ItemKind) => (r: RawItem): ReviewItem => {
  const hours = num(r.hours);
  const hourlyRate = num(r.intern_hourly_rate);
  const flatReward = num(kind === "proposal" ? r.estimated_reward : r.reward) ?? 0;

  return {
    key: `${kind}-${r.id}`,
    id: r.id,
    kind,
    title: r.title ?? (kind === "proposal" ? "Untitled proposal" : "Untitled assignment"),
    description: r.description ?? "",
    internName: r.intern_name,
    internPicture: r.intern_profile_picture ?? null,
    dueDate: (kind === "proposal" ? r.deadline : r.due_date) ?? null,
    reward: hours !== null && hourlyRate !== null ? hours * hourlyRate : flatReward,
    hours,
    hourlyRate,
    status: r.status,
    feedback: r.feedback ?? "",
    rejectionReason: r.rejection_reason ?? "",
    createdAt: r.created_at,
  };
};

// Mirrors the backend's REVIEWABLE_FROM: only these states can be acted on.
export const isReviewable = (item: ReviewItem): boolean =>
  item.kind === "proposal" ? item.status === "pending" : item.status === "completed";

export const fetchItems = (kind: ItemKind, p: ListParams) =>
  list<RawItem, ReviewItem>(kind, p, toItem(kind));

export const getItem = (kind: ItemKind, id: string | number) =>
  one<RawItem, ReviewItem>(kind, id, toItem(kind));

export interface ApprovePayload {
  hours?: number | null;
  reward?: number | null;
  message?: string;
}

// Hours-based items are paid hours x the intern's current rate by the backend,
// so send hours for those; flat-fee items send the reward amount instead.
export const approveItem = (item: ReviewItem, p: ApprovePayload) =>
  apiService.post(`${PATHS[item.kind]}/${item.id}/approve/`, {
    ...(item.hours !== null ? { approved_hours: p.hours } : { approved_reward: p.reward }),
    message: p.message ?? "",
  });

export const requestRevision = (item: ReviewItem, feedback: string, suggestedReward?: number | null) =>
  apiService.post(`${PATHS[item.kind]}/${item.id}/request-revision/`, {
    feedback,
    ...(suggestedReward !== null && suggestedReward !== undefined
      ? { suggested_reward: suggestedReward }
      : {}),
  });

export const rejectItem = (item: ReviewItem, reason: string) =>
  apiService.post(`${PATHS[item.kind]}/${item.id}/reject/`, { rejection_reason: reason });

export type CommentAction = "comment" | "approved" | "rejected" | "needs_revision";

export interface JobCardComment {
  id: number;
  body: string;
  action: CommentAction;
  author_details: {
    id: number;
    full_name: string;
    role: string;
    profile_picture: string | null;
  } | null;
  created_at: string;
}

export const commentsApi = {
  list: async (kind: ItemKind, id: number): Promise<JobCardComment[]> => {
    const res = await apiService.get<JobCardComment[]>(`${PATHS[kind]}/${id}/comments/`);
    return res.data;
  },
  create: async (kind: ItemKind, id: number, body: string): Promise<JobCardComment> => {
    const res = await apiService.post<JobCardComment>(`${PATHS[kind]}/${id}/comments/`, { body });
    return res.data;
  },
};

// ---------------------------------------------------------------- tasks ----

export interface TaskItem {
  id: number;
  taskId: string;
  title: string;
  description: string;
  internName: string;
  category: string;
  project: string | null;
  workDate: string;
  startTime: string;
  endTime: string;
  hoursLogged: number;
  hoursApproved: number | null;
  status: string;
  rejectionReason: string;
}

interface RawTask {
  id: number;
  task_id: string;
  title: string;
  description?: string | null;
  intern_name: string;
  category_name?: string | null;
  project_name?: string | null;
  work_date: string;
  start_time: string;
  end_time: string;
  hours_logged: string | number;
  hours_approved?: string | number | null;
  status: string;
  rejection_reason?: string | null;
}

const toTask = (r: RawTask): TaskItem => ({
  id: r.id,
  taskId: r.task_id,
  title: r.title,
  description: r.description ?? "",
  internName: r.intern_name,
  category: r.category_name ?? "",
  project: r.project_name ?? null,
  workDate: r.work_date,
  startTime: r.start_time?.slice(0, 5) ?? "",
  endTime: r.end_time?.slice(0, 5) ?? "",
  hoursLogged: Number(r.hours_logged),
  hoursApproved: num(r.hours_approved),
  status: r.status,
  rejectionReason: r.rejection_reason ?? "",
});

export const fetchTasks = (p: ListParams) => list<RawTask, TaskItem>("task", p, toTask);
export const getTask = (id: string | number) => one<RawTask, TaskItem>("task", id, toTask);

// Approving keeps the existing payroll rail: hours_approved on the entry.
export const approveTask = (task: TaskItem, hoursApproved: number) =>
  apiService.post(`${PATHS.task}/${task.id}/approve/`, { hours_approved: hoursApproved });

export const rejectTask = (task: TaskItem, reason: string) =>
  apiService.post(`${PATHS.task}/${task.id}/reject/`, { rejection_reason: reason });

// -------------------------------------------------------------- reports ----

export interface ReportItem {
  id: number;
  type: "individual" | "team";
  status: string;
  name: string;
  periodLabel: string;
  weekStart: string;
  metrics: { label: string; value: string }[];
}

interface RawReport {
  id: number;
  report_type: "individual" | "team";
  status: string;
  week_start: string;
  week_end: string;
  snapshot?: {
    period_label?: string;
    employee?: { full_name?: string };
    supervisor?: { full_name?: string };
    metrics?: Record<string, number>;
  };
}

const toReport = (r: RawReport): ReportItem => {
  const m = r.snapshot?.metrics ?? {};
  const metrics =
    r.report_type === "team"
      ? [
          { label: "Reports received", value: `${m.reports_received ?? 0}/${m.expected_reports ?? 0}` },
          { label: "Tasks completed", value: String(m.tasks_completed ?? 0) },
          { label: "Carry overs", value: String(m.carry_overs ?? 0) },
          { label: "Blockers", value: String(m.blockers ?? 0) },
        ]
      : [
          { label: "Completed", value: String(m.completed ?? 0) },
          { label: "Carry overs", value: String(m.carry_overs ?? 0) },
          { label: "Blockers", value: String(m.blockers ?? 0) },
          { label: "Leave days", value: String(m.leave_days ?? 0) },
        ];
  return {
    id: r.id,
    type: r.report_type,
    status: r.status,
    name:
      r.report_type === "team"
        ? `${r.snapshot?.supervisor?.full_name ?? "Team"} (team)`
        : (r.snapshot?.employee?.full_name ?? "Individual report"),
    periodLabel: r.snapshot?.period_label ?? `${r.week_start} to ${r.week_end}`,
    weekStart: r.week_start,
    metrics,
  };
};

export const fetchReports = (p: ListParams) => list<RawReport, ReportItem>("report", p, toReport);
export const getReport = (id: string | number) => one<RawReport, ReportItem>("report", id, toReport);

// The PDF endpoint needs the auth header, so fetch it as a blob and open that.
export const openReportPdf = async (id: number) => {
  const res = await apiService.get(`${PATHS.report}/${id}/download/`, {
    params: { output: "pdf" },
    responseType: "blob",
  });
  const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: "application/pdf" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ---------------------------------------------------- create assignment ----

export interface InternOption {
  id: number;
  name: string;
  hourlyRate: number | null;
}

interface RawIntern {
  id: number;
  hourly_rate?: string | number | null;
  user_details?: { full_name?: string };
}

export const fetchInterns = async (search = ""): Promise<InternOption[]> => {
  const res = await apiService.get("/api/job-card/internship/profiles/", {
    page_size: 6,
    is_active: true,
    ...(search.trim() ? { search: search.trim() } : {}),
  });
  const data = res.data as RawIntern[] | { results?: RawIntern[] };
  const rows = Array.isArray(data) ? data : (data.results ?? []);
  return rows
    .map((r) => ({
      id: r.id,
      name: r.user_details?.full_name ?? `Intern ${r.id}`,
      hourlyRate: num(r.hourly_rate),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export interface NewAssignment {
  intern: number;
  title: string;
  description: string;
  dueDate: string;
  reward: number | null;
  hours: number | null;
}

export const createAssignment = (a: NewAssignment) =>
  apiService.post(`${PATHS.assignment}/`, {
    intern: a.intern,
    title: a.title,
    description: a.description,
    ...(a.dueDate ? { due_date: a.dueDate } : {}),
    ...(a.hours !== null ? { hours: a.hours } : { reward: a.reward ?? 0 }),
  });
